import { useEffect, useState } from "react";
import { NavLink, Outlet, useLocation } from "react-router-dom";
import { useAuth } from "@/auth";
import { useIsMobile } from "@/hooks/useIsMobile";
import { KpiPeriodProvider } from "@/hooks/useKpiPeriod";
import { PeriodProvider } from "@/hooks/usePeriod";
import { useVisibleTabs, type NavItem } from "@/hooks/useVisibleTabs";
import { t } from "@/i18n";
import { TenantTheme } from "@/theme";
import { BrandMark, BrandName } from "./ui";
import { DateRangePicker } from "./DateRangePicker";
import { NavIcon } from "./NavIcon";

const WORK_TABS = new Set(["ozet", "kpi", "stok", "yorumlar", "denetim"]);

export function Layout() {
  return <PeriodProvider><KpiPeriodProvider><LayoutBody /></KpiPeriodProvider></PeriodProvider>;
}

function LayoutBody() {
  const { pathname } = useLocation();
  const isMobile = useIsMobile(1024);
  const tabs = useVisibleTabs().filter(tab => tab.visible);
  const { principal, logout } = useAuth();
  const tenantName = principal?.active_tenant?.name ?? t("app.title");
  const platform = principal?.active_tenant?.platform;
  const activeTab = tabs.find(tab => tab.path === pathname);
  return (
    <TenantTheme>
      <div className={`app-shell ${isMobile ? "app-shell-mobile" : "app-shell-desktop"}`}>
        <a href="#asakai-main" className="sr-only focus:not-sr-only focus:fixed focus:left-2 focus:top-2 focus:z-50 focus:rounded focus:bg-card focus:px-3 focus:py-2">{t("nav.skip")}</a>
        {!isMobile && <aside className="app-sidebar">
          <div className="sidebar-brand"><BrandMark size={42} className="rounded-lg bg-white" /><span className="font-display text-2xl font-bold tracking-tight">Asak<span className="font-normal text-[#a9bfff]">AI</span></span></div>
          <p className="px-6 text-xs text-[#a9bbce]">E-ticaret yönetim paneli</p>
          <DesktopNav tabs={tabs} />
          <div className="sidebar-bottom">
            <p className="mb-3 text-xs leading-relaxed text-[#a9bbce]">Mağazanızın verileri.<br />Karar vermek için tek bir yer.</p>
            <button type="button" onClick={() => void logout()} className="sidebar-link w-full"><NavIcon name="logout" />{t("nav.logout")}</button>
          </div>
        </aside>}
        <div className="app-workspace">
          <header className="workspace-header">
            {isMobile && <div className="flex items-center gap-2"><BrandMark size={34} /><BrandName className="text-lg" /></div>}
            <div className="workspace-store">
              <span className="store-avatar" aria-hidden="true">{tenantName.charAt(0)}</span>
              <div className="min-w-0"><p className="truncate text-sm font-semibold text-ink">{tenantName}</p><p className="text-xs text-muted">{platform ? ({ ikas: "ikas", ticimax: "Ticimax", ideasoft: "IdeaSoft", shopify: "Shopify" }[platform] ?? platform) : "Mağaza"}</p></div>
            </div>
            {!isMobile && <span className="ml-3 border-l border-line pl-5 text-sm text-muted">{activeTab ? t(activeTab.labelKey) : "Çalışma alanı"}</span>}
            {["/ozet", "/kpi"].includes(pathname) && <div className="workspace-date"><span className="text-xs text-muted">Rapor dönemi</span><DateRangePicker /></div>}
          </header>
          <main id="asakai-main" className={`workspace-main ${isMobile ? "pb-[var(--mobile-nav-h)]" : ""}`}><Outlet /></main>
          {!isMobile && <footer className="workspace-footer">AsakAI <span>·</span> {t("app.footer")}</footer>}
        </div>
        {isMobile && <MobileNav tabs={tabs} onLogout={() => void logout()} />}
      </div>
    </TenantTheme>
  );
}

function DesktopNav({ tabs }: { tabs: NavItem[] }) {
  return <nav aria-label={t("app.title")} data-testid="desktop-nav" className="sidebar-nav">
    {[{ label: "Mağaza", items: tabs.filter(tab => WORK_TABS.has(tab.id)) }, { label: "Yönetim", items: tabs.filter(tab => !WORK_TABS.has(tab.id)) }].filter(group => group.items.length).map(group => <div key={group.label} className="sidebar-group"><p className="sidebar-group-label">{group.label}</p>{group.items.map(tab => <NavLink key={tab.id} to={tab.path} className={({ isActive }) => `sidebar-link ${isActive ? "sidebar-link-active" : ""}`}><NavIcon name={tab.id} /><span>{t(tab.labelKey)}</span><TabBadge count={tab.badge} /></NavLink>)}</div>)}
  </nav>;
}

function TabBadge({ count }: { count?: number }) {
  if (!count || count <= 0) return null;
  return <span aria-label={t("nav.pending_badge", { count })} className="ml-auto inline-flex min-w-[18px] items-center justify-center rounded-full bg-amber px-1.5 py-0.5 text-[11px] font-semibold leading-none text-white">{count}</span>;
}

function MobileNav({ tabs, onLogout }: { tabs: NavItem[]; onLogout: () => void }) {
  const [moreOpen, setMoreOpen] = useState(false);
  const shown = tabs.slice(0, 4);
  const overflowTabs = tabs.slice(4);
  useEffect(() => {
    if (!moreOpen) return;
    const onKey = (event: KeyboardEvent) => { if (event.key === "Escape") setMoreOpen(false); };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [moreOpen]);
  return <>
    {moreOpen && <>
      <button type="button" className="fixed inset-0 z-20 cursor-default bg-navy/20" aria-label="Menüyü kapat" onClick={() => setMoreOpen(false)} />
      <nav aria-label={t("nav.more")} id="mobile-more" data-testid="mobile-nav-more-panel" className="mobile-more-panel">
        <p className="mb-2 px-3 text-xs font-semibold text-muted">Yönetim ve araçlar</p>
        {overflowTabs.map(tab => <NavLink key={tab.id} to={tab.path} onClick={() => setMoreOpen(false)} className={({ isActive }) => `flex min-h-[48px] items-center gap-3 rounded-lg px-3 text-sm font-medium ${isActive ? "bg-blue-pale text-brand-primary" : "text-ink"}`}><NavIcon name={tab.id} />{t(tab.labelKey)}<TabBadge count={tab.badge} /></NavLink>)}
        <button type="button" onClick={() => { setMoreOpen(false); onLogout(); }} className="mt-2 flex min-h-[48px] w-full items-center gap-3 border-t border-line px-3 text-sm text-red"><NavIcon name="logout" />{t("nav.logout")}</button>
      </nav>
    </>}
    <nav aria-label={t("app.title")} data-testid="mobile-nav" className="mobile-nav">
      {shown.map(tab => <NavLink key={tab.id} to={tab.path} className={({ isActive }) => `mobile-nav-link ${isActive ? "bg-blue-pale text-brand-primary" : "text-muted"}`}><NavIcon name={tab.id} size={19} /><span>{t(tab.labelKey)}</span><TabBadge count={tab.badge} /></NavLink>)}
      <button type="button" data-testid="mobile-nav-more-button" aria-controls="mobile-more" aria-expanded={moreOpen} onClick={() => setMoreOpen(open => !open)} className={`mobile-nav-link ${moreOpen ? "bg-blue-pale text-brand-primary" : "text-muted"}`}><NavIcon name="more" size={19} />{t("nav.more")}</button>
    </nav>
  </>;
}
